import { lazy } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import type { ModuleManifest } from '../../lib/host/types';
import { inspectionsKeys } from './queries/keys';

// Scheduling module (P05 §2, §8; W4 rows 1-2; stories 5-2a, 5-2b) — the create
// wizard and the field loop's day list.
//
// WHY A MODULE. The surface attaches through the module host, so `src/App.tsx`
// and `src/lib/registries/index.ts` are NOT edited by this story (DOD §1.8,
// "registries, not shell edits"). The manifest contributes the route and the nav
// row; deleting this folder leaves the platform compiling, which is the
// pluggability test P18 §1 sets.
//
// WHY `/app/schedule/new` AND NOT `/app/schedule`. W4 row 1 reaches the wizard
// from `/app/schedule`, and P05 §3 makes `/app/schedule` the CALENDAR — a
// month/week grid of chips. That surface is story **5-2b** (My day / calendar),
// which is a different story in this same wave. Squatting on its route with a
// wizard would mean 5-2b either inherits a redirect it has to unpick or has its
// own path renamed on arrival, and a half-built calendar here would be exactly
// the aspirational surface G-29 rejects. So this story takes the CHILD route it
// actually built, and 5-2b's "New inspection" button will point at it.
//
// WHY THE NAV ROW IS AN ACTION. Until 5-2b lands there is no `/app/schedule`
// index to link to, and a nav row pointing at a 404 is worse than none. The row
// therefore names what it does — "Schedule inspection" — and 5-2b replaces it
// with the calendar row it owns. Its `permission` matches the route's, so a
// persona who never sees the row cannot dead-end on the page either (story 2-20).
//
// ── STORY 5-2b: `/app/schedule` is now MY DAY, and the row points at it ────
// The replacement the note above promised, made. Three things changed and each
// one is the answer to a question the note left open:
//
//   * `/app/schedule` renders **My day** (P05 §8) — the mobile day list, not a
//     calendar. P05 §3's month/week grid, the workload board and the route view
//     are story **5-3**, which adds them ALONGSIDE this view on the same route
//     (its natural shape is a `view=` field next to My day's existing `mode=`
//     URL state). Shipping an empty grid here for 5-3 to fill would have been
//     exactly the aspirational surface G-29 rejects.
//   * The nav row is a DESTINATION now, not an action: "My day" → `/app/schedule`.
//     Its `permission` is `inspections:read`, NOT `inspections:create` — the row
//     is the field worker's home, and an inspector who may read their own
//     assignments but not create work must still be able to reach it. (The
//     "New inspection" CTA on the page keeps `inspections:create`, behind
//     `useCapabilityGate` — G-13.)
//   * The wizard keeps `/app/schedule/new` and loses its own nav row, because
//     W4 row 1 reaches it exactly the way it now does: "→ `/app/schedule` →
//     'New inspection'".
//
// `featureKeys: ['scheduling.core']` — the key P05 §5 lists as ON and the one the
// lifecycle routes already enforce. Deliberately NOT `inspections.assign`: the
// wizard's create half works on every plan, and only the ASSIGNEE step is paid
// (the backend masks `inspections:assign` behind that feature and answers with an
// upsell payload). Gating the whole surface on the paid key would hide creating
// an inspection from the plans that are entitled to it.
//
// NO `entities` / `search` block, so G-21 does not apply: this module introduces
// no new record type — an Inspection is an existing entity owned elsewhere.
export const manifest: ModuleManifest = {
  key: 'scheduling',
  featureKeys: ['scheduling.core'],
  nav: [
    {
      key: 'my-day',
      slot: 'main',
      label: 'My day',
      to: '/app/schedule',
      icon: CalendarDaysIcon,
      // Deliberately the READ capability — see the note above.
      permission: 'inspections:read',
      featureKey: 'scheduling.core',
      order: 25,
    },
  ],
  routes: [
    {
      // Story 5-2b. The scheduling surface at HEAD; 5-3 adds the calendar and
      // workload views alongside it on this same route.
      path: '/app/schedule',
      element: lazy(() => import('./pages/MyDayPage').then((m) => ({ default: m.MyDayPage }))),
      chrome: 'shell',
      // Matches the nav row: reaching your own day needs no right to create work.
      permission: 'inspections:read',
    },
    {
      path: '/app/schedule/new',
      element: lazy(() =>
        import('./pages/NewInspectionWizardPage').then((m) => ({ default: m.NewInspectionWizardPage })),
      ),
      chrome: 'shell',
      // Same gate as the row above. The ASSIGNEE step needs `inspections:assign`
      // too, but that is a step, not the surface: an inspector may legitimately
      // reach this page to create their own unassigned work.
      permission: 'inspections:create',
    },
  ],
  // P09 §2.1 parity — the capabilities that shipped WITH these endpoints in the
  // same stories (agents/src/capabilities/inspections.js). `inspection_my_day_list`
  // is 5-2b's: a READ, but the one the whole field surface exists for.
  agentTools: [
    'inspection_create',
    'inspection_assign',
    'inspection_eligible_assignees_list',
    'inspection_my_day_list',
  ],
  queryKeys: inspectionsKeys,
};
